import { useState, useEffect } from 'react';
import { web3, getETCMCLicenseContract, getETCPOWContract, convertWeiToEther } from '../utils/web3';
import { CONTRACT_ADDRESSES } from '../constants/contracts';
import { useNFTAPI } from './useNFTAPI';
import { useWallet } from './useWallet';

export const useContract = (account) => {
  const { isConnected } = useWallet();
  const [contract, setContract] = useState(null);
  const [etcpowContract, setEtcpowContract] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Initialize API hook
  const apiHook = useNFTAPI(account);

  const toHexQuantity = (value) => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string' && value.startsWith('0x')) return value;
    try {
      return web3.utils.toHex(web3.utils.toBN(value));
    } catch {
      return undefined;
    }
  };

  useEffect(() => {
    // Initialize contracts when account is available
    // Use a small delay to ensure web3 provider is ready
    if (account && window.ethereum) {
      const timer = setTimeout(() => {
        initializeContracts();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [account]);

  // Also try to initialize contracts when the component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!contract && window.ETCMCLICENSEV2_ABI) {
        initializeContracts();
      }
    }, 2000); // Wait 2 seconds for everything to be ready
    return () => clearTimeout(timer);
  }, []);

  const initializeContracts = async () => {
    try {
      // Initialize contracts silently

      // Wait a bit for Web3 to be ready
      await new Promise(resolve => setTimeout(resolve, 1000));

      const licenseContract = getETCMCLicenseContract(window.ETCMCLICENSEV2_ABI);
      const powContract = getETCPOWContract(window.ETCPOW_ABI);


      setContract(licenseContract);
      setEtcpowContract(powContract);
    } catch (error) {
      console.error('Error initializing contracts:', error);
    }
  };

  const getNFTs = async () => {
    // Use API only - much faster than blockchain
    try {
      const apiNFTs = await apiHook.fetchNFTsForSale();
      return apiNFTs;
    } catch (error) {
      console.error('API fetch failed:', error.message);
      throw error;
    }
  };

  
const getMyNFTs = async () => {
  // Use API only - much faster than blockchain
  if (!account) {
    console.warn('No account provided for getMyNFTs');
    return [];
  }
  
  try {
    const apiNFTs = await apiHook.fetchNFTsByOwner(account);
    return apiNFTs;
  } catch (error) {
    console.error('API fetch failed:', error.message);
    throw error;
  }
};


  
  const approveETCPOW = async (amount) => {
    if (!etcpowContract || !account) {
      throw new Error('Contract or account not available');
    }

    // Convert BigInt to string for ETC Classic compatibility
    const amountString = typeof amount === 'bigint' ? amount.toString() : amount;

    // Build tx data once so it is available for fallback handling
    const txData = etcpowContract.methods.approve(
      CONTRACT_ADDRESSES.ETCMCLICENSEV2,
      amountString
    ).encodeABI();

    try {
      // Debug logging

      const gasLimit = toHexQuantity(100000);
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });

      const txParams = {
        from: account,
        to: etcpowContract.options.address,
        data: txData,
        gas: gasLimit,
        gasPrice,
        value: '0x0'
      };


      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [txParams]
      });
      return tx;
    } catch (error) {
      console.error('Error approving ETCPOW:', error);

      // Handle the e.startsWith error - this is a MetaMask/internal formatting issue
      if (error?.message && error.message.includes('startsWith')) {
        try {
          const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });
          const tx = await window.ethereum.request({
            method: 'eth_sendTransaction',
            params: [
              {
                from: account,
                to: etcpowContract.options.address,
                data: txData,
                gasPrice,
                value: '0x0'
              }
            ]
          });
          return tx;
        } catch (fallbackError) {
          console.error('Fallback approval also failed:', fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  };

  const buyNFT = async (tokenId) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      // ETC Classic - manually build transaction with gas limits
      const txData = contract.methods.buyNFT(tokenId).encodeABI();

      const gasLimit = toHexQuantity(200000);
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });

      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });
      return tx;
    } catch (error) {
      console.error('Error buying NFT:', error);

      // Check if the error is due to license expiration
      if (error.message && (
        error.message.includes('License expired') ||
        error.message.includes('revert') ||
        error.message.includes('execution reverted')
      )) {
        // Refresh the NFT data since the license might have been burned
        setTimeout(() => {
          if (apiHook.triggerSync) {
            apiHook.triggerSync();
          }
        }, 2000); // Wait 2 seconds for blockchain confirmation

        throw new Error('This license has expired and been removed from the marketplace. The page will refresh automatically.');
      }

      throw error;
    }
  };

  const listNFTForSale = async (tokenId, price) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      // Convert price to wei
      const priceInWei = web3.utils.toWei(price.toString(), 'ether');

      // ETC Classic - manually build transaction with gas limits
      const txData = contract.methods.listNFT(tokenId, priceInWei).encodeABI();

      const gasLimit = toHexQuantity(150000);
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });

      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });
      return tx;
    } catch (error) {
      console.error('Error listing NFT:', error);

      // Check for common listing errors
      if (error.message && (
        error.message.includes('License expires too soon') ||
        error.message.includes('License expired')
      )) {
        throw new Error('This license cannot be listed because it expires too soon or has already expired.');
      }

      throw error;
    }
  };

  const getLegacyInfo = async () => {
    if (!contract || !account) {
      return { entitled: '0', claimed: '0', remaining: '0' };
    }

    try {
      const result = await contract.methods.getLegacyInfo(account).call();
      // Handle both array and object return types
      // Note: Entitlement and Claimed are counts of licenses (integers), not token balances
      if (Array.isArray(result)) {
        return {
          entitled: result[0].toString(),
          claimed: result[1].toString(),
          remaining: result[2].toString(),
        };
      } else if (result && typeof result === 'object') {
        return {
          entitled: (result.entitled || '0').toString(),
          claimed: (result.claimed || '0').toString(),
          remaining: (result.remaining || '0').toString(),
        };
      } else {
        console.warn('Unexpected return type from getLegacyInfo:', result);
        return { entitled: '0', claimed: '0', remaining: '0' };
      }
    } catch (error) {
      console.error('Error fetching legacy info:', error);
      return { entitled: '0', claimed: '0', remaining: '0' };
    }
  };

  const getETCPOWBalance = async () => {
    if (!etcpowContract || !account) {
      return '0';
    }

    try {
      const balance = await etcpowContract.methods.balanceOf(account).call();
      return convertWeiToEther(balance);
    } catch (error) {
      console.error('Error fetching ETCPOW balance:', error);
      return '0';
    }
  };

  const getLegacyClaimPrice = async () => {
    if (!contract) {
      return '0';
    }

    try {
      const result = await contract.methods.legacyClaimPrice().call();
      const formatted = convertWeiToEther(result);
      return formatted;
    } catch (error) {
      console.error('Error fetching legacy claim price:', error);
      return '0';
    }
  };

  const claimLegacyLicenses = async (amount) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      // Ensure amount is a BigInt and convert to string for contract
      const amountStr = amount.toString();
      
      // Convert to hex string for web3
      const amountHex = '0x' + amount.toString(16);
      
      // ETC Classic - manually build transaction with gas limits
      const txData = contract.methods.claimLegacy(amount).encodeABI();
      
      const gasLimit = toHexQuantity(300000);
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });
      
      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });
      return tx;
    } catch (error) {
      console.error('Error claiming legacy licenses:', error);
      throw error;
    }
  };

  const publicMintLicenses = async (tier, amount) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      const txData = contract.methods.publicMint(tier, amount).encodeABI();
      
      const gasLimit = toHexQuantity(400000);
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });
      
      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });
      return tx;
    } catch (error) {
      console.error('Error minting licenses:', error);
      throw error;
    }
  };
const getPublicPrices = async () => {
  if (!contract) return { 1: '0', 2: '0' };

  // Log connection status only once per session (reduce spam)
  if (!window._loggedConnectionStatus) {
    console.log('Contract address:', contract.options.address);
    console.log('Wallet connection:', isConnected ? 'Connected' : 'Disconnected');
    window._loggedConnectionStatus = true;
  }

  // Check current network (silent)

  // Check if contract exists at address
  try {
    const code = await web3.eth.getCode(contract.options.address);
    if (code === '0x' || code === '0x0') {
      console.error('No contract code found at address:', contract.options.address);
      return { 1: '0', 2: '0' };
    }
  } catch (codeError) {
    console.error('Error checking contract code:', codeError);
    return { 1: '0', 2: '0' };
  }

  // Check if contract methods are available
  if (!contract.methods || !contract.methods.getPublicTierPrice) {
    console.error('Contract methods not available. Please ensure your wallet is connected to Mordor testnet.');
    return { 1: '0', 2: '0' };
  }

  try {
    const prices = {};

    // Check both tiers 1 and 2
    for (let tier = 1; tier <= 2; tier++) {
      try {
        const price = await contract.methods.getPublicTierPrice(tier).call();
        const convertedPrice = convertWeiToEther(price);
        prices[tier] = convertedPrice;
      } catch (e) {
        console.error(`Error getting price for tier ${tier}:`, e.message);
        prices[tier] = '0';
      }
    }

    return prices;
  } catch (error) {
    console.error('Error fetching public prices:', error);
    return { 1: '0', 2: '0' };
  }
};

const getTierEnabled = async (tier) => {
  if (!contract) return false;

  try {
    const enabled = await contract.methods
      .tierEnabled(Number(tier))
      .call();

    return enabled;
  } catch (error) {
    console.error(`Error checking tier ${tier} enabled status:`, error);
    return false;
  }
};

const getMinListingPrice = async (tier) => {
  if (!contract) return '0';

  try {
    // Check if the method exists (prevents crash on older contract versions)
    if (!contract.methods.tierMinListingPrice) {
      return '0';
    }
    const price = await contract.methods.tierMinListingPrice(Number(tier)).call();
    return convertWeiToEther(price);
  } catch (error) {
    console.error(`Error fetching min listing price for tier ${tier}:`, error);
    return '0';
  }
};


  const getFeatureFlags = async () => {
    if (!contract) {
      return { legacyEnabled: false, publicEnabled: false };
    }
    
    try {
      const result = await contract.methods.getFeatureFlags().call();
      
      // The contract returns an array [legacyEnabled, publicEnabled]
      if (Array.isArray(result)) {
        return {
          legacyEnabled: result[0],
          publicEnabled: result[1]
        };
      } else if (result && typeof result === 'object') {
        return {
          legacyEnabled: result.legacyEnabled || false,
          publicEnabled: result.publicEnabled || false
        };
      } else {
        return { legacyEnabled: false, publicEnabled: false };
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      return { legacyEnabled: false, publicEnabled: false };
    }
  };

  const cancelListing = async (tokenId) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      // ETC Classic - manually build transaction with gas limits
      const txData = contract.methods.cancelListing(tokenId).encodeABI();
      
      const gasLimit = toHexQuantity(150000);
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });
      
      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });
      return tx;
    } catch (error) {
      console.error('Error cancelling listing:', error);
      throw error;
    }
  };

  const checkAndBurnExpired = async (tokenId) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      // ETC Classic - manually build transaction with gas limits
      const txData = contract.methods.checkAndBurnExpired(tokenId).encodeABI();

      const gasLimit = toHexQuantity(100000);
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });

      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });
      return tx;
    } catch (error) {
      console.error('Error checking/burning expired license:', error);
      throw error;
    }
  };

  const mintLifetime = async (to, tier, amount) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      // Ensure amount is a BigInt and convert to string for contract
      const amountStr = amount.toString();

      // ETC Classic - manually build transaction
      const txData = contract.methods.mintLifetime(to, tier, amountStr).encodeABI();

      const gasLimit = toHexQuantity(500000); // Higher gas limit for batch minting
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });

      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });

      return tx;
    } catch (error) {
      console.error('Error minting lifetime licenses:', error);
      throw error;
    }
  };

  const mintWithExpiry = async (to, tier, amount, expiryTimestamp) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      // Ensure parameters are strings for contract
      const amountStr = amount.toString();
      const expiryStr = expiryTimestamp.toString();

      // ETC Classic - manually build transaction
      const txData = contract.methods.mintWithExpiry(to, tier, amountStr, expiryStr).encodeABI();

      const gasLimit = toHexQuantity(500000); // Higher gas limit for batch minting
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });

      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });

      return tx;
    } catch (error) {
      console.error('Error minting licenses with custom expiry:', error);
      throw error;
    }
  };


  const getRenewEnabled = async () => {
    if (!contract) {
      console.error('Contract not initialized in getRenewEnabled');
      return false;
    }

    try {
      const enabled = await contract.methods.renewEnabled().call();
      return enabled;
    } catch (error) {
      console.error('Error checking renew enabled status:', error);
      return false;
    }
  };

  const getRemainingLicenseTime = async (tokenId) => {
    if (!contract) {
      console.error('Contract not initialized in getRemainingLicenseTime');
      return 0;
    }

    try {
      const remainingTime = await contract.methods.getRemainingLicenseTime(tokenId).call();
      return remainingTime.toString();
    } catch (error) {
      console.error('Error getting remaining license time:', error);
      return 0;
    }
  };

  const renewLicense = async (tokenId) => {
    if (!contract || !account) {
      throw new Error('Contract or account not available');
    }

    try {
      // ETC Classic - manually build transaction with gas limits
      const txData = contract.methods.renewLicense(tokenId).encodeABI();

      const gasLimit = toHexQuantity(200000);
      const gasPrice = await window.ethereum.request({ method: 'eth_gasPrice' });

      const tx = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: account,
            to: contract.options.address,
            data: txData,
            gas: gasLimit,
            gasPrice,
            value: '0x0'
          }
        ]
      });
      return tx;
    } catch (error) {
      console.error('Error renewing license:', error);
      
      // Check for common renewal errors
      if (error.message && (
        error.message.includes('Renewals disabled') ||
        error.message.includes('Not owner') ||
        error.message.includes('License expired') ||
        error.message.includes('Already maxed')
      )) {
        if (error.message.includes('Renewals disabled')) {
          throw new Error('License renewals are currently disabled by the administrator.');
        } else if (error.message.includes('Not owner')) {
          throw new Error('You can only renew licenses that you own.');
        } else if (error.message.includes('License expired')) {
          throw new Error('Cannot renew expired licenses.');
        } else if (error.message.includes('Already maxed')) {
          throw new Error('This license has already reached the maximum duration of 18 months.');
        }
      }

      throw error;
    }
  };

  const getTier = async (tokenId) => {
    if (!contract) {
      return 1;
    }

    try {
      const tier = await contract.methods.getTier(tokenId).call();
      return tier;
    } catch (error) {
      console.error('Error getting token tier:', error);
      return 1;
    }
  };

  return {
    contract,
    etcpowContract,
    isLoading,
    getNFTs,           // API-based fetching
    getMyNFTs,         // API-based fetching
    getPublicPrices,   // Blockchain - for pricing info
    getLegacyInfo,     // Blockchain - for legacy claims
    getLegacyClaimPrice, // Blockchain - for legacy claims
    getETCPOWBalance,  // Blockchain - for balance checks
    getFeatureFlags,   // Blockchain - for feature toggles
    getTierEnabled,    // Blockchain - for tier availability
    getMinListingPrice, // Blockchain - for marketplace constraints
    getRenewEnabled,   // Blockchain - for renewal system status
    getRemainingLicenseTime, // Blockchain - for renewal eligibility
    getTier,           // Blockchain - for getting token tier
    approveETCPOW,     // Blockchain transaction
    buyNFT,           // Blockchain transaction
    listNFTForSale,   // Blockchain transaction
    cancelListing,    // Blockchain transaction
    claimLegacyLicenses, // Blockchain transaction
    publicMintLicenses,   // Blockchain transaction
    renewLicense,         // Blockchain transaction
    mintLifetime,         // Blockchain transaction (admin only - lifetime licenses)
    mintWithExpiry,       // Blockchain transaction (admin only - custom expiry)
    checkAndBurnExpired,  // Blockchain transaction
    // Utility functions for expiration handling
    getExpirationInfo: (expiresAt) => {
      // Handle various falsy/zero values that indicate lifetime
      if (!expiresAt || expiresAt === '0' || expiresAt === 0 || expiresAt === 'null' || expiresAt === null) {
        return { isLifetime: true, remainingDays: null, remainingTime: 'Lifetime', isExpired: false, canList: true };
      }

      const now = Math.floor(Date.now() / 1000);
      const expiresAtNum = parseInt(expiresAt);

      if (expiresAtNum <= now) {
        return { isLifetime: false, remainingDays: 0, remainingTime: 'Expired', isExpired: true };
      }

      const remainingSeconds = expiresAtNum - now;
      const remainingDays = Math.floor(remainingSeconds / 86400);

      let remainingTime;
      if (remainingDays > 0) {
        remainingTime = `${remainingDays} days`;
      } else {
        const hours = Math.floor(remainingSeconds / 3600);
        if (hours > 0) {
          remainingTime = `${hours} hours`;
        } else {
          const minutes = Math.floor(remainingSeconds / 60);
          remainingTime = `${minutes} minutes`;
        }
      }

      return {
        isLifetime: false,
        remainingDays,
        remainingTime,
        isExpired: false,
        canList: remainingDays >= 7 // MIN_REMAINING_FOR_LISTING = 7 days
      };
    },

    // API status and utilities
    apiError: apiHook.error,
    syncStatus: apiHook.syncStatus,
    checkApiHealth: apiHook.checkHealth,
    triggerSync: apiHook.triggerSync,
    triggerFullSync: apiHook.triggerFullSync
  };
};
